import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api, PublicData } from '../../Infrastructure/api';
import { RootState } from '../types';
import { resetAppState } from '../actions/resetAppState';

interface DemoState {
  publicData: PublicData | null;
  isLoading: boolean;
  error: string | null;
  campaignCode: string | null;
}

const createInitialState = (): DemoState => ({
  publicData: null,
  isLoading: false,
  error: null,
  campaignCode: null,
});

const initialState: DemoState = createInitialState();

export const fetchDemoContentByCampaignCode = createAsyncThunk<
  PublicData,
  string,
  { rejectValue: string }
>('demo/fetchByCampaignCode', async (campaignCode, { rejectWithValue }) => {
  try {
    const response = await api.public.getContent(campaignCode);
    return response;
  } catch (error: unknown) {
    let errorMessage = 'Failed to fetch demo content';
    if (typeof error === 'object' && error !== null && 'response' in error) {
        const response = (error as { response?: { data?: { message?: string } } }).response;
        if (response?.data?.message) {
            errorMessage = response.data.message;
        }
    } else if (error instanceof Error) {
        errorMessage = error.message;
    }
    return rejectWithValue(errorMessage);
  }
});

export const demoSlice = createSlice({
  name: 'demo',
  initialState,
  reducers: {
    setCampaignCode: (state, action: PayloadAction<string | null>) => {
      state.campaignCode = action.payload;
    },
    clearDemoContent: () => createInitialState()
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDemoContentByCampaignCode.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.publicData = null;
      })
      .addCase(fetchDemoContentByCampaignCode.fulfilled, (state, action) => {
        state.isLoading = false;
        state.publicData = action.payload;
      })
      .addCase(fetchDemoContentByCampaignCode.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'An unknown error occurred';
      })
      .addCase(resetAppState, () => createInitialState());
  },
});

export const { setCampaignCode, clearDemoContent } = demoSlice.actions;

export const selectDemoData = (state: RootState) => state.demo.publicData;
export const selectDemoIsLoading = (state: RootState) => state.demo.isLoading;
export const selectDemoError = (state: RootState) => state.demo.error;
export const selectCampaignCode = (state: RootState) => state.demo.campaignCode;

export default demoSlice.reducer; 